import { useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Code, Group, MultiSelect, NumberInput, Select, SimpleGrid, Stack, Text, TextInput, Textarea, Title } from '@mantine/core'
import backend from '../../backend/backend'
import PageHeader from '../../components/shared/PageHeader'
import { useAction, useCan, useResource, useT } from '../../hooks'
import Message from '../../i18n/Message'
import { keys, loadSettings } from '../../store/dataActionCreators'

const randomKey = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(32))
  return btoa(String.fromCharCode(...bytes))
}

const INTEGRATIONS = [
  ['encryptionConfigured', 'config_encryption_label'],
  ['storageConfigured', 'config_storage_label'],
  ['paystackConfigured', 'config_paystack_label'],
  ['emailBound', 'config_email_label'],
  ['smsConfigured', 'config_sms_label'],
]

export default function Settings() {
  const t = useT()
  const { data, reload } = useResource(keys.settings, loadSettings)
  const canEdit = useCan('settings.manage')
  const canRun = useCan('jobs.run')
  const { busy, run } = useAction()
  const [form, setForm] = useState(null)
  const [key, setKey] = useState('')
  useEffect(() => {
    if (data) setForm({ ...data.settings, company: { ...data.settings.company } })
  }, [data])
  if (!form) return null
  const set = (k) => (v) => setForm({ ...form, [k]: v })
  const save = () => run(() => backend.saveSettings(form), { success: 'settings_saved', onDone: reload })
  return (
    <>
      <PageHeader title="settings_title" />
      <Stack maw={760} gap="lg">
        <Card>
          <Title order={5} mb="sm"><Message id="settings_security" /></Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <NumberInput label={t('field_idle_minutes')} min={5} max={60} value={form.idle_timeout_minutes} onChange={set('idle_timeout_minutes')} disabled={!canEdit} />
            <Select label={t('field_mfa_policy')} allowDeselect={false} value={form.mfa_policy} onChange={set('mfa_policy')} disabled={!canEdit}
              data={[{ value: 'all', label: t('mfa_policy_all') }, { value: 'admins_only', label: t('mfa_policy_admins') }]} />
          </SimpleGrid>
          <MultiSelect mt="sm" label={t('field_required_docs')} value={form.required_documents} onChange={set('required_documents')} disabled={!canEdit}
            data={['government_id', 'customer_photo', 'proof_of_address'].map((d) => ({ value: d, label: t(`doc_${d}`) }))} />
        </Card>
        <Card>
          <Title order={5} mb="sm"><Message id="settings_company" /></Title>
          <Stack>
            <TextInput label={t('field_company_name')} value={form.company.name} disabled={!canEdit} onChange={(e) => set('company')({ ...form.company, name: e.currentTarget.value })} />
            <Textarea label={t('field_payment_instructions')} autosize minRows={2} value={form.payment_instructions || ''} disabled={!canEdit} onChange={(e) => set('payment_instructions')(e.currentTarget.value)} />
            <TextInput label={t('field_agreement_version')} value={form.agreement_version} disabled={!canEdit} onChange={(e) => set('agreement_version')(e.currentTarget.value)} />
            <Textarea label={t('field_agreement')} autosize minRows={6} maxRows={16} value={form.agreement_template} disabled={!canEdit} onChange={(e) => set('agreement_template')(e.currentTarget.value)} />
            <Alert color="yellow"><Message id="agreement_legal_note" /></Alert>
          </Stack>
        </Card>
        {canEdit && <Button onClick={save} loading={busy} w="fit-content"><Message id="save" /></Button>}

        <Card>
          <Title order={5} mb="sm"><Message id="settings_integrations" /></Title>
          <Stack gap={6}>
            {INTEGRATIONS.map(([k, label]) => (
              <Group key={k} justify="space-between">
                <Text size="sm"><Message id={label} /></Text>
                <Badge color={data.config[k] ? 'green' : 'orange'}>{t(data.config[k] ? 'config_ok' : 'config_missing')}</Badge>
              </Group>
            ))}
          </Stack>
        </Card>
        <Card>
          <Title order={5} mb="xs"><Message id="key_generator_title" /></Title>
          <Text size="sm" c="dimmed" mb="sm"><Message id="key_generator_hint" /></Text>
          <Button variant="default" w="fit-content" onClick={() => setKey(randomKey())}><Message id="key_generate" /></Button>
          {key && <Code block mt="sm" style={{ wordBreak: 'break-all' }}>{key}</Code>}
        </Card>
        {canRun && (
          <Card>
            <Title order={5} mb="xs"><Message id="jobs_title" /></Title>
            <Text size="sm" c="dimmed" mb="sm"><Message id="jobs_hint" /></Text>
            <Button variant="default" w="fit-content" loading={busy} onClick={() => run(() => backend.runJobs(), { success: 'jobs_done' })}><Message id="jobs_run" /></Button>
          </Card>
        )}
      </Stack>
    </>
  )
}
